import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import GoogleMapComponent from '../../../components/GoogleMapComponent';
import { sharePropertyViaWhatsApp } from '../../../utils/whatsappShare';
import DocumentAttachment from '../../../components/ui/DocumentAttachment';
import { getDocuments, saveDocuments } from '../../../utils/documentManager';
import Video from '../../../components/AppVideo';


const PropertyDetails = ({ property, onClose, onDelete, userRole, customFields = [] }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  const tabs = [
    { id: 'overview', label: 'نظرة عامة', icon: 'Home' },
    { id: 'details', label: 'التفاصيل', icon: 'Info' },
    { id: 'location', label: 'الموقع', icon: 'MapPin' },
    { id: 'features', label: 'المميزات', icon: 'Star' },
    { id: 'documents', label: 'المستندات', icon: 'FileText' },
    { id: 'clients', label: 'العملاء المهتمون', icon: 'Users' },
    { id: 'owner', label: 'المالك', icon: 'User' },
    { id: 'pricing', label: 'التسعير', icon: 'Banknote' },
    { id: 'status', label: 'الحالة', icon: 'Activity' }
  ];

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (property) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [property]);

  // Handle ESC key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e?.key === 'Escape' && property) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [property, onClose]);

  if (!property) {
    return (
      <div className="bg-card rounded-lg border border-border p-6 shadow-warm h-full flex items-center justify-center">
        <div className="text-center text-muted-foreground">
          <Icon name="Building2" size={48} className="mx-auto mb-3 opacity-50" />
          <p className="text-sm">اختر عقاراً لعرض التفاصيل</p>
        </div>
      </div>
    );
  }

  const formatCurrency = (amount, currency) => {
    const formatted = new Intl.NumberFormat('ar-EG')?.format(amount);
    return `${formatted} ${currency}`;
  };

  const handleDeleteProperty = () => {
    setShowDeleteModal(true);
  };

  const [propertyDocuments, setPropertyDocuments] = useState([]);

  useEffect(() => {
    if (property?.propertyId) {
      const docs = getDocuments('property', property?.propertyId);
      setPropertyDocuments(docs);
    }
  }, [property?.propertyId]);

  const handleDocumentsChange = (docs) => {
    setPropertyDocuments(docs);
    if (property?.propertyId) {
      saveDocuments('property', property?.propertyId, docs);
    }
  };

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { duration: 0.15, ease: 'easeOut' }
    },
    exit: { 
      opacity: 0,
      transition: { duration: 0.1, ease: 'easeIn' }
    }
  };

  const modalVariants = {
    hidden: { 
      opacity: 0,
      x: 300,
      scale: 0.95
    },
    visible: { 
      opacity: 1,
      x: 0,
      scale: 1,
      transition: { 
        duration: 0.25,
        ease: [0.16, 1, 0.3, 1]
      }
    },
    exit: { 
      opacity: 0,
      x: 300,
      scale: 0.95,
      transition: { 
        duration: 0.2,
        ease: 'easeIn'
      }
    }
  };

  return (
    <AnimatePresence mode="wait">
      {property && (
        <motion.div
          className="fixed inset-0 z-50 flex items-end md:items-center justify-end p-0 md:p-4"
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={overlayVariants}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Modal Content - Slide from right on desktop, bottom on mobile */}
          <motion.div
            className="relative bg-card rounded-t-2xl md:rounded-lg border border-border shadow-warm-xl w-full md:w-[500px] lg:w-[600px] h-[90vh] md:max-h-[90vh] flex flex-col"
            variants={modalVariants}
            onClick={(e) => e?.stopPropagation()}
          >
            <div className="p-4 border-b border-border flex items-center justify-between bg-muted/30">
              <h3 className="text-base md:text-lg font-heading font-semibold text-foreground">
                تفاصيل العقار
              </h3>
              <Button
                variant="ghost"
                size="icon"
                iconName="X"
                iconSize={20}
                onClick={onClose}
              />
            </div>
            <motion.div 
              className="flex-1 overflow-y-auto p-4 space-y-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1, duration: 0.2 }}
            >
              <div className="aspect-[4/3] rounded-lg overflow-hidden">
                <Image
                  src={property?.image}
                  alt={property?.imageAlt}
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="space-y-3">
                <div>
                  <p className="text-xs text-muted-foreground font-caption mb-1">معرف العقار</p>
                  <p className="text-sm font-data font-medium text-foreground">{property?.propertyId}</p>
                </div>

                <div>
                  <p className="text-xs text-muted-foreground font-caption mb-1">العنوان</p>
                  <p className="text-sm text-foreground">{property?.address}</p>
                  <p className="text-xs text-muted-foreground mt-1">{property?.district}</p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-muted-foreground font-caption mb-1">النوع</p>
                    <p className="text-sm text-foreground">{property?.type}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground font-caption mb-1">نوع العقار</p>
                    <p className="text-sm text-foreground">{property?.realEstateType}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-muted-foreground font-caption mb-1">الحالة</p>
                    <p className="text-sm text-foreground">{property?.status}</p>
                  </div>
                </div>

                {property?.remarks && (
                  <div className="bg-muted/30 p-3 rounded-lg border border-border">
                    <p className="text-xs text-muted-foreground font-caption mb-2 flex items-center gap-1">
                      <Icon name="MessageSquare" size={14} color="var(--color-muted-foreground)" />
                      ملاحظات
                    </p>
                    <p className="text-sm text-foreground leading-relaxed">{property?.remarks}</p>
                  </div>
                )}

                <div>
                  <p className="text-xs text-muted-foreground font-caption mb-1">السعر</p>
                  <p className="text-lg font-data font-bold text-primary" dir="ltr">
                    {formatCurrency(property?.price, property?.currency)}
                  </p>
                </div>

                <div className="border-t border-border pt-3">
                  <p className="text-xs text-muted-foreground font-caption mb-2">معلومات المالك</p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Icon name="User" size={16} color="var(--color-muted-foreground)" />
                      <span className="text-sm text-foreground">{property?.owner}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon name="Phone" size={16} color="var(--color-muted-foreground)" />
                      <span className="text-sm text-foreground font-data" dir="ltr">{property?.ownerPhone}</span>
                    </div>
                  </div>
                </div>

                {property?.customFields && Object.keys(property?.customFields)?.length > 0 && (
                  <div className="border-t border-border pt-3">
                    <p className="text-xs text-muted-foreground font-caption mb-3 flex items-center gap-1">
                      <Icon name="List" size={14} color="var(--color-muted-foreground)" />
                      الحقول المخصصة
                    </p>
                    <div className="space-y-2">
                      {customFields?.map(field => {
                        const value = property?.customFields?.[field?.id];
                        if (!value) return null;
                        return (
                          <div key={field?.id}>
                            <p className="text-xs text-muted-foreground font-caption">{field?.label}</p>
                            <p className="text-sm text-foreground mt-0.5">
                              {field?.type === 'checkbox' ? (value ? 'نعم' : 'لا') : value}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {property?.videos && property?.videos?.length > 0 && (
                  <div className="border-t border-border pt-3">
                    <p className="text-xs text-muted-foreground font-caption mb-2 flex items-center gap-1">
                      <Icon name="Video" size={14} color="var(--color-muted-foreground)" />
                      فيديوهات العقار
                    </p>
                    <div className="space-y-2">
                      {property?.videos?.map((video, index) => (
                        <div key={index} className="rounded-lg overflow-hidden border border-border">
                          <Video
                            src={video}
                            controls
                            loading="lazy"
                            preload="metadata"
                            className="w-full aspect-video object-cover"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="border-t border-border pt-3">
                  <p className="text-xs text-muted-foreground font-caption mb-2">الموقع على الخريطة</p>
                  <div className="rounded-lg overflow-hidden border border-border">
                    <GoogleMapComponent
                      latitude={property?.lat}
                      longitude={property?.lng}
                      draggable={false}
                      zoom={15}
                      height="300px"
                      onLocationSelect={() => {}}
                    />
                  </div>
                </div>

                {activeTab === 'documents' && (
                  <div className="space-y-4">
                    <div className="bg-muted/30 rounded-lg p-4 border border-border">
                      <h3 className="text-base font-heading font-semibold text-foreground mb-2">
                        مستندات العقار
                      </h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        جميع المستندات والملفات المتعلقة بهذا العقار
                      </p>
                    </div>
                    <DocumentAttachment
                      documents={propertyDocuments}
                      onChange={handleDocumentsChange}
                      entityType="property"
                      entityId={property?.propertyId}
                    />
                  </div>
                )}
              </div>
            </motion.div>
            <motion.div 
              className="p-4 border-t border-border bg-muted/30 space-y-2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.2 }}
            >
              <Button 
                variant="default" 
                fullWidth 
                iconName="Share2" 
                iconPosition="right"
                onClick={() => sharePropertyViaWhatsApp(property)}
              >
                مشاركة عبر واتساب
              </Button>
              <Button variant="default" fullWidth iconName="Edit" iconPosition="right">
                تعديل العقار
              </Button>
              <Button variant="outline" fullWidth iconName="FileText" iconPosition="right">
                إنشاء عقد
              </Button>
              <Button variant="outline" fullWidth iconName="MessageSquare" iconPosition="right">
                التواصل مع المالك
              </Button>
              {userRole === 'admin' && (
                <Button 
                  variant="outline" 
                  fullWidth 
                  iconName="Trash2" 
                  iconPosition="right"
                  onClick={() => onDelete?.(property?.id)}
                  className="text-error border-error hover:bg-error/10"
                >
                  حذف العقار
                </Button>
              )}
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PropertyDetails;